import { Box, Chip, Stack, Typography } from '@mui/material'
import { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { matchPath, useLocation } from 'react-router-dom'
import { makeStyles } from 'tss-react/mui'
import { strategies } from '../../util/strategyBuilder'
import { CategoryGroupNameToCode, StrategyGroup } from '../strategyTemplate/StrategyTemplate.types'
import { selectStrategyTemplates } from '../../selectors/strategyTemplate'
import { fetchStrategyTemplates } from '../../services/apis/strategyTemplate'
import { StrategyTemplateProto } from '../../proto/generated/strategy'
import { parseStrategyTemplateOrders, strategyParser } from '../strategyTemplate/StrategyParser'
import useSelectPrimaryOrUnderlyingSecurity from '../../hooks/useSelectPrimaryOrUnderlyingSecurity'
import { selectUnderlyingOptionsTradeData } from '../../selectors/options'
import { selectOrderType } from '../../selectors/strategyBuilder'
import { setPredefinedStrategy, setStrategyGroup, setViewTabs, updateTradeOrders } from '../../reducer/strategyBuilder.reducer'
import { OptionChainViewKey, STRATEGY_LOGOS_BASE_URL } from '../../assets/constants'
import ROUTES from '../../assets/routes'
import commonActions from '../../actions/common.actions'
import { WidgetListItems } from '../../util/sidebar'
import { selectSimulatorDate } from '../../selectors/simulator'
import { selectExpandedRows } from '../../selectors/home'
import { fetchOptionChainV2 } from '../../util/options'
import { GetHistoricOptionChainRequest } from '../../services/apis/simulator'
import { optionsTradesDataType } from '../../pages/typings/options.typings'

const STRATEGY_TABS = [StrategyGroup.BULLISH, StrategyGroup.BEARISH, StrategyGroup.NEUTRAL] as const

const useStyles = makeStyles()((theme) => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        padding: theme.spacing(1.5),
        gap: theme.spacing(1.5),
        overflow: 'hidden',
    },
    tabs: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(1),
        flexShrink: 0,
    },
    chip: {
        borderRadius: 16,
        height: 28,
        fontSize: theme.typography.body2.fontSize,
    },
    grid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
        gap: theme.spacing(1),
        overflowY: 'auto',
        minHeight: 0,
        alignContent: 'start',
        paddingBottom: theme.spacing(1),
    },
    card: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: theme.spacing(0.75),
        padding: theme.spacing(1.25, 1),
        borderRadius: theme.shape.borderRadius,
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        cursor: 'pointer',
        minHeight: 88,
        textAlign: 'center',
        transition: theme.transitions.create(['border-color', 'background-color'], {
            duration: theme.transitions.duration.shorter,
        }),
        '&:hover': {
            borderColor: theme.palette.primary.main,
            backgroundColor: theme.palette.action.hover,
        },
    },
    cardDisabled: {
        cursor: 'not-allowed',
        opacity: 0.6,
        pointerEvents: 'none',
    },
    icon: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 40,
        height: 28,
        '& svg, & img': {
            width: 40,
            height: 28,
        },
    },
    label: {
        fontSize: 12,
        lineHeight: 1.3,
        color: theme.palette.text.primary,
        width: '100%',
    },
}))

const getStrategyIcon = (name: string, imageUrl?: string) => {
    if (imageUrl) {
        return <img src={`${STRATEGY_LOGOS_BASE_URL}${imageUrl}.svg`} alt="" />
    }
    return strategies.find((s) => s.label === name)?.icon ?? null
}

const TradePanelReadyMadeStrategies = () => {
    const { classes, cx } = useStyles()
    const dispatch = useDispatch()
    const location = useLocation()
    const [selectedGroup, setSelectedGroup] = useState<StrategyGroup>(StrategyGroup.BULLISH)
    const [loading, setLoading] = useState(false)
    const strategyTemplates = useSelector(selectStrategyTemplates)
    const orderType = useSelector(selectOrderType)
    const simulatorTimestamp = useSelector(selectSimulatorDate)
    const { primaryOrUnderlying: mainSecurity, primaryOrUnderlyingToken: underlyingToken } = useSelectPrimaryOrUnderlyingSecurity()
    const optionExpiries = useSelector(selectUnderlyingOptionsTradeData(underlyingToken))

    const optionChainViewKey = useMemo(
        () => (matchPath(ROUTES.simulator, location.pathname) ? OptionChainViewKey.HISTORICAL : OptionChainViewKey.LIVE),
        [location.pathname]
    )
    const expandedRows = useSelector(selectExpandedRows(optionChainViewKey))

    useEffect(() => {
        if (strategyTemplates.length === 0) {
            fetchStrategyTemplates()
        }
    }, [strategyTemplates.length])

    const filteredStrategies = useMemo(
        () => strategyTemplates.filter((s) => CategoryGroupNameToCode[s.group as any] === selectedGroup),
        [strategyTemplates, selectedGroup]
    )

    const resolveOptionExpiries = useCallback(async (): Promise<optionsTradesDataType[]> => {
        if (optionExpiries?.length) {
            const hasTrades = optionExpiries.some((e) => e.trades?.length > 0)
            if (hasTrades) return optionExpiries
        }
        if (optionChainViewKey === OptionChainViewKey.HISTORICAL && mainSecurity && simulatorTimestamp) {
            const payload: GetHistoricOptionChainRequest = {
                security: {
                    code: mainSecurity.code,
                    exchange: mainSecurity.exchange
                },
                referenceTimestamp: simulatorTimestamp
            }
            const rows = expandedRows.length > 0 ? expandedRows : ['0']
            const { optionsData } = await fetchOptionChainV2(
                payload,
                dispatch,
                optionExpiries,
                'both',
                true,
                rows,
                simulatorTimestamp
            )
            return optionsData ?? []
        }
        return optionExpiries ?? []
    }, [optionExpiries, optionChainViewKey, mainSecurity, simulatorTimestamp, expandedRows, dispatch])

    const handleStrategyClick = useCallback(
        async (template: StrategyTemplateProto) => {
            if (loading || !mainSecurity) return
            setLoading(true)
            try {
                const resolvedExpiries = await resolveOptionExpiries()
                if (optionChainViewKey === OptionChainViewKey.LIVE) {
                    const tradeOrders = await parseStrategyTemplateOrders(template, mainSecurity, resolvedExpiries, orderType)
                    commonActions.dispatchRightSideWidget(dispatch, WidgetListItems.TRADE)
                    dispatch(setPredefinedStrategy(template.name))
                    dispatch(setStrategyGroup(`${mainSecurity.displayName} - ${template.name}`))
                    dispatch(updateTradeOrders(tradeOrders))
                    dispatch(setViewTabs(true))
                } else {
                    await strategyParser(template, mainSecurity, resolvedExpiries, optionChainViewKey)
                    dispatch(setPredefinedStrategy(template.name))
                    dispatch(setStrategyGroup(`${mainSecurity.displayName} - ${template.name}`))
                    commonActions.dispatchRightSideWidget(dispatch, WidgetListItems.TRADE)
                }
            } catch (e) {
                console.error('Failed to apply ready-made strategy', e)
            } finally {
                setLoading(false)
            }
        },
        [loading, mainSecurity, optionChainViewKey, orderType, dispatch, resolveOptionExpiries]
    )

    return (
        <Box className={classes.root}>
            <Stack className={classes.tabs} direction="row">
                {STRATEGY_TABS.map((group) => {
                    const isActive = group === selectedGroup
                    return (
                        <Chip
                            key={group}
                            label={group}
                            clickable
                            color={isActive ? 'primary' : 'default'}
                            onClick={() => setSelectedGroup(group)}
                            className={classes.chip}
                            data-event="Trade Panel Strategy Group Changed"
                            data-event-strategy_group={group}
                        />
                    )
                })}
            </Stack>
            <Box className={classes.grid}>
                {filteredStrategies.map((strategy) => (
                    <Box
                        key={strategy.id}
                        className={cx(classes.card, loading && classes.cardDisabled)}
                        onClick={() => handleStrategyClick(strategy)}
                        data-event="Trade Panel Strategy Selected"
                        data-event-strategy-name={strategy.name}
                    >
                        <Box className={classes.icon}>{getStrategyIcon(strategy.name, strategy.imageUrl)}</Box>
                        <Typography className={classes.label} variant="body2">
                            {strategy.name}
                        </Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    )
}

export default memo(TradePanelReadyMadeStrategies)
